import {
  ArrowRightIcon,
  CircleCheck,
  CircleX,
  Info,
  LoaderCircle,
  Star,
  TriangleAlert,
  Paperclip,
  File,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { RunStatus } from '../../../../../../../../../backend/src/types/types';
import { DocProcessingErrorType } from '../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { useGetCase } from '@/api/queries/useGetCase';
import { Doc } from '@/api/types/backend-dto-types';
import Chip from '@/components/atoms/chip';
import { Button } from '@/components/atoms/new-button';
import ChildDocumentsPopover from '@/components/molecules/ChildDocumentsPopover';
import EditableField from '@/components/molecules/EditableField';
import { ThemesTooltip } from '@/components/organisms/ThemesTooltip';
import { isProcessingStatus } from '@/constants/pipelineStatus';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';

const DocumentRow = ({
  doc,
  caseId,
  searchQuery,
}: {
  doc: Doc & {
    hasKeyFacts: boolean;
    hasRelevantFacts: boolean;
    chunkMatchCount: number;
  };
  caseId: string;
  searchQuery?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { mutate: updateDocFileName, isLoading: isUpdatingFileName } = useUpdateDoc();
  const { mutate: updateDocAuthor, isLoading: isUpdatingAuthor } = useUpdateDoc();
  const { mutate: updateDocDate, isLoading: isUpdatingDate } = useUpdateDoc();

  const { data: caseData } = useGetCase(caseId ?? '');

  const isCaseReprocessing =
    caseData?.runs?.at(0) && caseData.runs.length > 1 && isProcessingStatus(caseData.runs[0].status as RunStatus);

  const navigateToParentDocument = () => {
    if (doc.file.parentFile?.doc?.id) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('docId', doc.file.parentFile.doc.id);
      navigate(location.pathname + '?' + searchParams.toString());
    }
  };

  const handleViewDoc = (docId: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('docId', docId);
    navigate(location.pathname + '?' + searchParams.toString());
  };

  const handleUpdateDocFileName = (value: string) => {
    updateDocFileName({ caseId, docId: doc.id, field: 'filename', value });
    trackCustomEvent(TrackingEvents.UpdateDocumentName, { documentId: doc.id });
  };

  const handleUpdateDocAuthor = (value: string) => {
    updateDocAuthor({ caseId, docId: doc.id, field: 'author', value });
    trackCustomEvent(TrackingEvents.UpdateDocumentAuthor, { documentId: doc.id });
  };

  const handleUpdateDocDate = (value: string) => {
    updateDocDate({ caseId, docId: doc.id, field: 'dateText', value });
    trackCustomEvent(TrackingEvents.UpdateDocumentDate, { documentId: doc.id });
  };

  const hasFileError = doc.pipelineErrorType === DocProcessingErrorType.FileError;

  const processStates = {
    notProcessed: { icon: <CircleX size={14} />, text: 'Failed to Process' },
    unableToOpen: { icon: <TriangleAlert size={14} />, text: 'Unable to open' },
    processed: { icon: <CircleCheck size={14} className="text-green-500" />, text: 'Processed' },
    processing: {
      icon: <LoaderCircle size={14} className="animate-spin text-gray-500" />,
      text: 'Processing...',
    },
  };

  let processState = processStates.notProcessed;

  if (hasFileError) {
    processState = processStates.unableToOpen;
  } else if (doc.isProcessed) {
    processState = processStates.processed;
  } else if (isCaseReprocessing) {
    processState = processStates.processing;
  }

  const failedOrUnprocessed =
    processState === processStates.notProcessed || processState === processStates.unableToOpen;

  const formatAuthorString = (author: string | undefined | null) => {
    if (author === undefined || author === null) {
      return '';
    }

    if (author.length < 50) {
      return author;
    }

    const authors = author.split(', ');
    let trimmedAuthorString = '';

    for (const authorItem of authors) {
      trimmedAuthorString += authorItem;

      if (trimmedAuthorString.length >= 50) {
        return `${trimmedAuthorString} ...`;
      }

      trimmedAuthorString += ', ';
    }

    return `${author.slice(0, 50)} ...`;
  };

  return (
    <div
      className={cn(
        processState === processStates.notProcessed && 'border-red-500 text-red-600 shadow-red-50',
        processState === processStates.unableToOpen && 'border-amber-500 text-amber-500 shadow-amber-50',
        'flex flex-col rounded-lg border p-4 pb-3 shadow-sm transition-shadow hover:shadow-md',
      )}
    >
      {/* Header Section */}
      <div className={cn('mb-3 border-b pb-3', 'flex items-center justify-between')}>
        <div className="flex flex-1 items-center gap-2">
          <div className="font-semibold">
            <EditableField value={doc.file.name} onSave={handleUpdateDocFileName} isLoading={isUpdatingFileName} />
          </div>
        </div>
        {failedOrUnprocessed ? null : (
          <div className="ml-4 flex items-center gap-2">
            <Chip className="bg-gray-100 text-gray-600">{doc.file.extension.toUpperCase()}</Chip>

            {doc.type && <Chip className="bg-brandTertiary/10 text-brandTertiary">{doc.type}</Chip>}

            {doc.issues && doc.issues.length > 0 && <ThemesTooltip issues={doc.issues} />}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-4">
        {/* Metadata Section */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {/* Parent document information */}
          {doc.file.parentFile && (
            <div className="flex items-center gap-2 text-blue-600">
              <File size={14} />
              <span className="font-medium">Attachment of:</span>
              <Link
                className="underline hover:text-blue-700"
                to={(() => {
                  if (doc.file.parentFile?.doc?.id) {
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.set('docId', doc.file.parentFile.doc.id);
                    return location.pathname + '?' + searchParams.toString();
                  }
                  return location.pathname;
                })()}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {doc.file.parentFile.name}
              </Link>
            </div>
          )}

          {/* Child documents count */}
          {doc.file.childFiles && doc.file.childFiles.length > 0 && (
            <ChildDocumentsPopover childFiles={doc.file.childFiles}>
              <button className="flex items-center gap-2 text-green-600 transition-colors hover:text-green-700">
                <Paperclip size={14} />

                <span className="font-medium underline underline-offset-4">
                  {doc.file.childFiles.length} attachment{doc.file.childFiles.length !== 1 ? 's' : ''}
                </span>
              </button>
            </ChildDocumentsPopover>
          )}

          <div className="flex items-center gap-2 text-gray-600">
            <span className="font-medium">Author:</span>
            <EditableField
              value={formatAuthorString(doc.author) ?? ''}
              onSave={handleUpdateDocAuthor}
              isLoading={isUpdatingAuthor}
            />
          </div>

          <div className="flex items-center gap-2 text-gray-600">
            <span className="font-medium">Date:</span>
            <EditableField value={formatDate(doc.date ?? '')} onSave={handleUpdateDocDate} isLoading={isUpdatingDate} />
          </div>
        </div>

        {/* Summary Section */}
        <div className="flex items-center gap-1.75 text-sm text-gray-600">
          {processState === processStates.notProcessed ? (
            <>
              <Info size={13} className="pb-0.25" />
              <p>
                Something went wrong when we tried to process this document. You can still attempt to manually add
                facts.
              </p>
            </>
          ) : processState === processStates.unableToOpen ? (
            <>
              <Info size={13} className="pb-0.25" />
              <p>We weren't able to open this file. This can happen if the file is password-protected or corrupted.</p>
            </>
          ) : (
            doc.summary
          )}
        </div>

        {/* Footer Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="primary"
              size="xs"
              className="pl-3"
              onClick={() => {
                trackCustomEvent(TrackingEvents.ClickViewDocument, { documentId: doc.id });
                handleViewDoc(doc.id);
              }}
            >
              View Document
              <ArrowRightIcon />
            </Button>

            {doc.chunkMatchCount !== undefined && doc.chunkMatchCount > 0 && (
              <Button
                variant="secondary"
                size="xs"
                className="animate-fadeIn"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.ClickViewDocument, { documentId: doc.id, withSearch: true });
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set('docId', doc.id);
                  if (searchQuery) {
                    searchParams.set('searchQuery', searchQuery);
                  }
                  navigate(location.pathname + '?' + searchParams.toString());
                }}
              >
                Matches in content: {doc.chunkMatchCount}
                <ArrowRightIcon />
              </Button>
            )}
          </div>

          <div className="flex items-end gap-4">
            <div className="flex items-center gap-1.5">
              <Chip className="flex items-center gap-1.5 rounded-md bg-gray-100 py-0.75">
                <span className="font-medium">Facts:</span> <b>{doc.factsCount || 0}</b>
              </Chip>

              {doc.hasKeyFacts && (
                <Chip className="flex items-center gap-1.5 rounded-md bg-yellow-100 py-0.75 text-yellow-700">
                  <Star size={12} />
                  <span className="text-xs font-medium">Contains Key Facts</span>
                </Chip>
              )}
              {doc.hasRelevantFacts && (
                <Chip className="flex items-center gap-1.5 rounded-md bg-green-100 py-0.75 text-green-700">
                  <CircleCheck size={12} className="text-green-700" />
                  <span className="text-xs font-medium">Contains Relevant Facts</span>
                </Chip>
              )}
            </div>

            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-1.5 text-xs">
                {processState.icon}
                <span className="pt-0.5 font-medium">{processState.text}</span>
              </div>
              <div className="text-xs text-gray-500">Uploaded {formatDate(doc.createdDate)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentRow;
